import React, { useEffect } from "react";
import { Box, Button, Flex, Text } from "ui-components";
import { COLORS, ROBOTO } from "ui-components/theme";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import map from "lodash/map";
import filter from "lodash/filter";
import sortBy from "lodash/sortBy";
import isFunction from "lodash/isFunction";
import includes from "lodash/includes";
import {
  urlParam,
  safeEval,
  getChannelFromUrl,
  getPaymentOption,
  getPaymentOptionFromUrl
} from "utils/helpers";
import { PHONE_PAYMENT_OPTIONS } from "utils/constants";
import FormattedMessage from "components/FormattedMessage";
import CheckboxField from "components/FormFields/CheckboxField";
import NumberSlider from "components/NumberSlider";
import StaticContentBox from "components/StaticContentBox/index";

const PhoneDetails = ({
  phones,
  change,
  formData,
  data,
  themeColors,
  channelPhonePaymentOptions
}) => {
  const {
    showPaymentCheckBox,
    showPaymentInfo,
    paymentTermSlider,
    ippSupportedBankContentBoxes,
    paymentOptionsInfo
  } = data || {};
  const { phoneColorCode, phoneCapacity, phoneVariant, paymentTerm } =
    formData || {};
  const phoneId = urlParam("id");
  const phone = find(phones, { id: phoneId || get(phoneVariant, "phoneId") });
  const selectedPhonePaymentOption =
    get(formData, "phonePaymentOption") || getPaymentOptionFromUrl();
  const phonePaymentOption = getPaymentOption(
    selectedPhonePaymentOption,
    channelPhonePaymentOptions,
    phoneVariant
  );
  const phonePaymentOptions =
    get(channelPhonePaymentOptions, [getChannelFromUrl()]) || {};
  const banks = phonePaymentOptions?.banks || data?.banks || {};
  const ippSupportedBanks =
    phonePaymentOptions?.ippSupportedBanks || data?.ippSupportedBanks || {};
  const showSupportedBanks =
    ippSupportedBanks &&
    paymentTerm &&
    phonePaymentOption === PHONE_PAYMENT_OPTIONS.IPP;
  useEffect(
    () => {
      if (phoneId && phoneVariant && phoneVariant.phoneId !== phoneId) {
        resetSelection();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phone, phoneId, phoneVariant]
  );

  const getPhoneVariant = data => {
    const { colorCode = phoneColorCode, capacity = phoneCapacity } = data || {};
    return find(get(phone, "allPhoneVariants"), variant => {
      return variant.colorCode === colorCode && variant.capacity === capacity;
    });
  };

  useEffect(() => {
    isFunction(change) &&
      change(
        "phoneVariant",
        getPhoneVariant({ colorCode: phoneColorCode, capacity: phoneCapacity })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTerm]);

  useEffect(
    () => {
      if (
        isFunction(change) &&
        get(formData, "phonePaymentOption") !== phonePaymentOption
      ) {
        change("phonePaymentOption", phonePaymentOption);
        change("phoneVariant", getPhoneVariant());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phonePaymentOption]
  );

  if (isEmpty(phone)) return null;

  const getCapacitiesForColor = () => {
    const allPhoneVariantsSelectedColor = filter(
      phone.allPhoneVariants,
      variant => {
        return variant.colorCode === phoneColorCode;
      }
    );

    return sortBy(
      map(allPhoneVariantsSelectedColor, variant => variant.capacity)
    );
  };

  const selectCheckBox = event => {
    change("phonePaymentCheckbox", event.target.checked);
  };

  const selectColor = ({ code, name }) => {
    change("phoneColorCode", code);
    change("phoneColorName", name);
    change("phoneCapacity", null);
    change("phoneVariant", null);
    change("phonePaymentOption", null);
  };

  const selectCapacity = capacity => {
    change("phoneCapacity", capacity);
    change(
      "phonePaymentOption",
      phonePaymentOption || PHONE_PAYMENT_OPTIONS.FULL
    );
    change("phoneVariant", getPhoneVariant({ capacity }));
  };

  const selectPaymentOption = paymentOption => {
    change("phonePaymentOption", paymentOption);
    change("phoneVariant", getPhoneVariant());
  };

  const resetSelection = () => {
    change("paymentTerm", null);
    change("phonePaymentCheckbox", null);
    change("phoneColorCode", null);
    change("phoneColorName", null);
    change("phoneCapacity", null);
    change("phoneVariant", null);
    change("phonePaymentOption", null);
    change("checkBoxValue", null);
  };

  const renderColorButton = ({ name, code }) => {
    const selected = phoneColorCode === code;
    const size = selected ? [22, 28, 32, 34] : [20, 26, 30, 32];
    return (
      <Box key={code} px={0.5} mx={1} flexWrap={"wrap"}>
        <Button
          onClick={() => {
            selectColor({ name, code });
          }}
          minWidth="20px"
          sx={{
            padding: ["0.25rem", "0.5rem", "0.5rem"],
            borderColor: selected ? "blue.4" : "gray.0",
            backgroundColor: "white !important",
            borderRadius: "50%"
          }}
          variant="outline"
          key={name}
        >
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: "50%",
              backgroundColor: code
            }}
          />
        </Button>
      </Box>
    );
  };

  const renderPaymentTermSlider = () => {
    const { condition, enable, label, field, data } = paymentTermSlider;
    // eslint-disable-next-line no-eval
    const canRender =
      !isEmpty(phoneVariant) && safeEval(get, formData, condition, true);
    if (canRender && enable) {
      // from ims
      const phoneVariant = formData?.phoneVariant || {};
      // from configmanager
      const defaultInstallmentMonth =
        phonePaymentOptions?.defaultInstallments || data?.defaultValue;
      const minInstallmentMonth =
        phoneVariant?.minInstallmentMonth ||
        phonePaymentOptions?.minInstallments ||
        data?.min;
      const maxInstallmentMonth =
        phoneVariant?.maxInstallmentMonth ||
        phonePaymentOptions?.maxInstallments ||
        data?.max;
      const installmentStep =
        phoneVariant?.installmentStep ||
        phonePaymentOptions?.stepInstallments ||
        data?.step ||
        6;
      const supportedInstallments = phonePaymentOptions?.supportedInstallments;
      let markList = {};
      let count = minInstallmentMonth;
      const defaultTerm = parseInt(
        formData?.paymentTerm || defaultInstallmentMonth || maxInstallmentMonth
      );
      while (count && maxInstallmentMonth && count <= maxInstallmentMonth) {
        let data = count;
        if (
          isEmpty(supportedInstallments) ||
          includes(supportedInstallments, data)
        ) {
          markList[data] = data;
        }
        count = count + installmentStep;
      }
      return (
        <Box flexDirection="column" mb={1}>
          <NumberSlider
            field={field}
            label={label}
            marks={isEmpty(markList) ? data?.marks : markList}
            min={minInstallmentMonth}
            max={maxInstallmentMonth}
            onChange={paymentTerm => {}}
            step={null}
            defaultValue={defaultTerm}
            formValues={formData}
            labelMessage={"payment_term"}
            justifyContent={"center"}
          />
        </Box>
      );
    }
  };

  const renderSupportedBanks = () => {
    const supportedBanks = map(get(ippSupportedBanks, `${paymentTerm}`), bank =>
      get(banks, [bank])
    );

    return !isEmpty(phoneVariant) && !isEmpty(supportedBanks) ? (
      <Box textAlign="center" flexDirection="column">
        <Box mb={6}>
          <FormattedMessage
            html
            id={"following_banks_provide_this_instalment"}
            values={formData}
          />
        </Box>

        <Flex justifyContent="center" mb={4}>
          {map(supportedBanks, card => (
            <Box mx="1" key={card.alt} width={0.3}>
              <img
                style={{ height: "auto", width: "auto" }}
                src={card.src}
                alt={card.alt}
              />
            </Box>
          ))}
        </Flex>

        <Flex justifyContent="center" flexDirection="column">
          {map(ippSupportedBankContentBoxes, box => (
            <Box mx="1" key={get(box, "content")} width={1}>
              <StaticContentBox {...box} themeColors={themeColors} />
            </Box>
          ))}
        </Flex>
      </Box>
    ) : null;
  };

  const renderPhonePaymentCheckBox = () => {
    const { condition, enable, label, field } = showPaymentCheckBox;
    // eslint-disable-next-line no-eval
    const canRender = safeEval(get, formData, condition, true);
    if (canRender && enable) {
      return (
        <Box flexDirection="column">
          <CheckboxField
            onChange={selectCheckBox}
            field={field}
            label={label}
            // input={{ checked: phonePaymentCheckbox }}
          />
        </Box>
      );
    }
  };

  const renderPaymentInfo = () => {
    const { condition, enable, content } = showPaymentInfo;
    // eslint-disable-next-line no-eval
    const canRender = safeEval(get, formData, condition, true);
    if (canRender && enable) {
      const style = {
        backgroundColor: COLORS.cyan[0],
        padding: "12px",
        borderRadius: "12px"
      };
      return (
        <Box sx={style} flexDirection="column" mb={1}>
          <Text mb={1} fontSize={1} fontFamily={ROBOTO} fontWeight="400">
            <FormattedMessage html id={content} />
          </Text>
        </Box>
      );
    }
  };

  const renderColorOptions = () => {
    return (
      <Box mb={4}>
        <Text mb={1} fontSize={3} fontFamily={ROBOTO} fontWeight="700">
          <FormattedMessage id="choose_color" />
        </Text>

        <Flex mx={-1} mb={1} flexWrap="wrap">
          {map(phone.variantColors, renderColorButton)}
        </Flex>
      </Box>
    );
  };

  const renderStorageOptions = () => {
    return (
      <Box mb={5}>
        <Text mb={1} fontSize={3} fontFamily={ROBOTO} fontWeight="700">
          <FormattedMessage id="choose_capacity" />
        </Text>

        <Flex mx={-1} flexWrap="wrap">
          {map(getCapacitiesForColor(), capacity => {
            const selected = phoneCapacity === capacity;

            return (
              <Button
                onClick={() => selectCapacity(capacity)}
                width={[0.45, 0.3]}
                minWidth={"60px"}
                sx={{
                  padding: ["0.5rem", "0.5rem", "0.75rem"],
                  borderColor: selected ? "blue.4" : "gray.0",
                  backgroundColor: "white !important"
                }}
                variant="outline"
                key={capacity}
                mx={1}
              >
                <Text
                  fontSize={["0.8rem", "0.9rem", "1rem"]}
                  fontFamily="Roboto"
                  fontWeight={selected ? "700" : "400"}
                >
                  {capacity} GB
                </Text>
              </Button>
            );
          })}
        </Flex>
      </Box>
    );
  };

  const renderPaymentOptions = () => {
    const phoneVariant = getPhoneVariant();
    if (isEmpty(phoneVariant)) return null;

    const sortedPaymentOptionsInfo = sortBy(paymentOptionsInfo, ["index"]);

    return (
      <Flex flexDirection="column" mb={7}>
        <Text
          mb={1}
          mt={2}
          fontSize={3}
          fontFamily={ROBOTO}
          fontWeight="700"
          textAlign="center"
        >
          <FormattedMessage id="payment_options" />
        </Text>

        <Flex mx={-1} flexWrap={"wrap"} justifyContent="center">
          {renderInstallmentPaymentOption(phoneVariant)}
          {renderSplitPaymentOption(phoneVariant)}
          {renderFullPaymentOption(phoneVariant)}
        </Flex>
        {/* Add additional info under the paymentOption buttons */}
        {sortedPaymentOptionsInfo && sortedPaymentOptionsInfo.length > 0 && (
          <Flex mx={-1} flexWrap={"wrap"} justifyContent="center">
            {sortedPaymentOptionsInfo.map(data => {
              const label = get(data, "label");
              return (
                <Box width={[1 / 2, 1 / 3, 1 / 3]} px={1} mb={"0.5rem"}>
                  {label && <FormattedMessage html id={label} />}
                </Box>
              );
            })}
          </Flex>
        )}
      </Flex>
    );
  };

  const renderInstallmentPaymentOption = phoneVariant => {
    const eligible =
      get(
        phoneVariant,
        `paymentOption.${PHONE_PAYMENT_OPTIONS.IPP}.eligible`
      ) && get(phonePaymentOptions, [PHONE_PAYMENT_OPTIONS.IPP], false);
    if (!eligible) return null;
    const {
      installmentMonths,
      formattedInstallmentCost,
      discount,
      formattedOriginalInstallmentCost
    } = phoneVariant;
    return (
      <Box width={[1 / 2, 1 / 3, 1 / 3]} px={1} mb={"0.5rem"}>
        <Button
          onClick={() => {
            selectPaymentOption(PHONE_PAYMENT_OPTIONS.IPP);
          }}
          minHeight={120}
          width={1}
          height="100%"
          sx={{
            "&:hover": {
              backgroundColor: "white"
            },
            borderRadius: "1.5rem",
            borderColor:
              phonePaymentOption === PHONE_PAYMENT_OPTIONS.IPP
                ? "blue.4"
                : "gray.0"
          }}
          variant="outline"
        >
          <Text fontFamily="Roboto" mb={2}>
            <FormattedMessage id="pay_amount_now" values={{ amount: "$0" }} />
          </Text>
          <Text fontFamily="Roboto" mb={1}>
            <FormattedMessage
              id="pay_amount_monthly"
              values={{ amount: formattedInstallmentCost }}
            />
          </Text>
          {discount > 0 && (
            <Text
              fontFamily="Roboto"
              fontWeight={300}
              my={1}
              style={{ textDecoration: "line-through" }}
            >
              <FormattedMessage
                id="pay_amount_monthly"
                values={{ amount: formattedOriginalInstallmentCost }}
              />
            </Text>
          )}
          <Text fontFamily="Roboto" size="sm" fontWeight={300}>
            <FormattedMessage
              id="installment_months_on_supported_card"
              values={{ installmentMonths }}
            />
          </Text>
        </Button>
      </Box>
    );
  };

  const renderSplitPaymentOption = phoneVariant => {
    const eligible =
      get(
        phoneVariant,
        `paymentOption.${PHONE_PAYMENT_OPTIONS.SPLIT}.eligible`
      ) && get(phonePaymentOptions, [PHONE_PAYMENT_OPTIONS.SPLIT], false);
    if (!eligible) return null;
    const {
      installmentMonths,
      formattedComboUpfrontCost,
      formattedComboInstallmentCost,
      discount,
      formattedOriginalComboInstallmentCost
    } = phoneVariant;
    return (
      <Box width={[1 / 2, 1 / 3, 1 / 3]} px={1} mb={"0.5rem"}>
        <Button
          onClick={() => {
            selectPaymentOption(PHONE_PAYMENT_OPTIONS.SPLIT);
          }}
          minHeight={120}
          width={1}
          height="100%"
          sx={{
            "&:hover": {
              backgroundColor: "white"
            },
            borderRadius: "1.5rem",
            borderColor:
              phonePaymentOption === PHONE_PAYMENT_OPTIONS.SPLIT
                ? "blue.4"
                : "gray.0"
          }}
          variant="outline"
        >
          <Text fontFamily="Roboto" mb={2}>
            <FormattedMessage
              id="pay_amount_now"
              values={{ amount: formattedComboUpfrontCost }}
            />
          </Text>
          <Text fontFamily="Roboto" mb={1}>
            <FormattedMessage
              id="pay_amount_monthly"
              values={{ amount: formattedComboInstallmentCost }}
            />
          </Text>
          {discount > 0 && (
            <Text
              fontFamily="Roboto"
              fontWeight={300}
              my={1}
              style={{ textDecoration: "line-through" }}
            >
              <FormattedMessage
                id="pay_amount_monthly"
                values={{ amount: formattedOriginalComboInstallmentCost }}
              />
            </Text>
          )}
          <Text fontFamily="Roboto" size="sm" fontWeight={300}>
            <FormattedMessage
              id="installment_months_on_supported_card"
              values={{ installmentMonths }}
            />
          </Text>
        </Button>
      </Box>
    );
  };

  const renderFullPaymentOption = phoneVariant => {
    const eligible =
      get(
        phoneVariant,
        `paymentOption.${PHONE_PAYMENT_OPTIONS.FULL}.eligible`
      ) && get(phonePaymentOptions, [PHONE_PAYMENT_OPTIONS.FULL], false);
    if (!eligible) return null;
    const { formattedCost, discount, formattedOriginalCost } = phoneVariant;
    return (
      <Box width={[1 / 2, 1 / 3, 1 / 3]} px={1} mb={"0.5rem"}>
        <Button
          onClick={() => {
            selectPaymentOption(PHONE_PAYMENT_OPTIONS.FULL);
          }}
          minHeight={120}
          width={1}
          height="100%"
          sx={{
            "&:hover": {
              backgroundColor: "white"
            },
            borderRadius: "1.5rem",
            borderColor:
              phonePaymentOption === PHONE_PAYMENT_OPTIONS.FULL
                ? "blue.4"
                : "gray.0"
          }}
          variant="outline"
        >
          <Text fontFamily="Roboto" mb={2}>
            <FormattedMessage id="full" />
          </Text>
          <Text fontFamily="Roboto">{formattedCost}</Text>
          {discount > 0 && (
            <Text
              fontFamily="Roboto"
              fontWeight={300}
              my={1}
              style={{ textDecoration: "line-through" }}
            >
              {formattedOriginalCost}
            </Text>
          )}
          <Text fontFamily="Roboto" size="sm" fontWeight={300}>
            <FormattedMessage id="due_on_checkout" />
          </Text>
          <Text fontFamily="Roboto" size="sm" fontWeight={300}>
            <FormattedMessage id="all_cards_accepted" />
          </Text>
        </Button>
      </Box>
    );
  };

  return (
    <Box
      px={6}
      variant="main"
      maxWidth="600px"
      mx="auto"
      padding={["1rem", "1.5rem"]}
      style={{ marginBottom: window.stickyFooterHeight }}
    >
      <Box mb={3}>
        <Text
          fontSize={["1.2rem", "1.4rem", "1.6rem", "1.8rem"]}
          fontFamily="Roboto"
          fontWeight="900"
          textAlign="center"
          color="black.2"
        >
          {phone.name}
        </Text>
      </Box>

      <Flex>
        <Box width={[0.3]}>
          <img src={phone.phoneImage} alt="phone" />
        </Box>
        <Box width={[0.7]} ml={6} mt={5}>
          {renderColorOptions()}
          {phoneColorCode && renderStorageOptions()}
        </Box>
      </Flex>
      <Flex flexDirection="column">
        {getPhoneVariant() && renderPaymentOptions()}
      </Flex>

      <Flex flexDirection="column">
        {showPaymentCheckBox && renderPhonePaymentCheckBox()}
        {showPaymentInfo && renderPaymentInfo()}
        {paymentTermSlider && renderPaymentTermSlider()}
        {showSupportedBanks && renderSupportedBanks()}
      </Flex>
    </Box>
  );
};

export default PhoneDetails;
